import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import Step1 from './steps/Step1';
import Step2 from './steps/Step2';
import Step3 from './steps/Step3';
import Step4 from './steps/Step4';
import Step5 from './steps/Step5';
import SuccessStep from './steps/SuccessStep';

const FormWizard = ({ 
  currentStep, 
  setCurrentStep, 
  formData, 
  updateFormData, 
  resetForm 
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [protocolNumber, setProtocolNumber] = useState(null);

  // Reset protocol number when going back to step 1
  useEffect(() => {
    if (currentStep === 1) {
      setProtocolNumber(null);
    }
  }, [currentStep]);

  const steps = [
    { id: 1, title: 'Στοιχεία Αιτούντος', component: Step1 },
    { id: 2, title: 'Δικαιολογητικά', component: Step2 },
    { id: 3, title: 'Είδος Αιτήματος', component: Step3 },
    { id: 4, title: 'Στοιχεία Αιτήματος', component: Step4 },
    { id: 5, title: 'Επιβεβαίωση', component: Step5 }
  ];

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // Δημιουργία FormData για file upload
      const formDataToSend = new FormData();
      
      // Προσθήκη JSON fields
      formDataToSend.append('requester_type', formData.requesterType);
      formDataToSend.append('request_type', formData.requestType);
      formDataToSend.append('identity_method', formData.identityMethod);
      if (formData.patientAmka) formDataToSend.append('patient_amka', formData.patientAmka);
      if (formData.patientName) formDataToSend.append('patient_name', formData.patientName);
      if (formData.periodOfHospitalization) formDataToSend.append('period_of_hospitalization', formData.periodOfHospitalization);
      if (formData.clinic) formDataToSend.append('clinic', formData.clinic);
      if (formData.visitDate) formDataToSend.append('visit_date', formData.visitDate);
      if (formData.examDate) formDataToSend.append('exam_date', formData.examDate);
      if (formData.surgeryDate) formDataToSend.append('surgery_date', formData.surgeryDate);
      if (formData.admissionDate) formDataToSend.append('admission_date', formData.admissionDate);
      if (formData.timePeriod) formDataToSend.append('time_period', formData.timePeriod);
      if (formData.examTypes) formDataToSend.append('exam_types', JSON.stringify(formData.examTypes));
      if (formData.examDates) formDataToSend.append('exam_dates', JSON.stringify(formData.examDates));
      if (formData.requestDetails) formDataToSend.append('request_details', formData.requestDetails);
      if (formData.comments) formDataToSend.append('comments', formData.comments);
      if (formData.email) formDataToSend.append('email', formData.email);
      if (formData.phone) formDataToSend.append('phone', formData.phone);

      // Collect attachment types
      const attachmentTypes = [];

      // Add identity document if uploaded
      if (formData.identityDocument) {
        formDataToSend.append('attachments', formData.identityDocument);
        attachmentTypes.push('identity_document');
      }

      // Add family certificate if uploaded (DL2)
      if (formData.familyCertificate) {
        formDataToSend.append('attachments', formData.familyCertificate);
        attachmentTypes.push('family_certificate');
      }

      // Add will certificate if uploaded (DL2)
      if (formData.willCertificate) {
        formDataToSend.append('attachments', formData.willCertificate);
        attachmentTypes.push('will_certificate');
      }

      // Add general attachments
      if (formData.attachments && formData.attachments.length > 0) {
        formData.attachments.forEach((file) => {
          formDataToSend.append('attachments', file);
          attachmentTypes.push('general');
        });
      }

      // Send attachment types array
      if (attachmentTypes.length > 0) {
        formDataToSend.append('attachment_types', JSON.stringify(attachmentTypes));
      }

      // Validation checks
      if (!formData.requesterType) {
        toast.error('Παρακαλώ επιλέξτε τύπο αιτούντος');
        setIsSubmitting(false);
        return;
      }
      
      if (!formData.identityMethod) {
        toast.error('Παρακαλώ επιλέξτε μέθοδο επιβεβαίωσης ταυτότητας');
        setIsSubmitting(false);
        return;
      }

      if (!formData.identityDocument) {
        toast.error('Παρακαλώ επισυνάψτε το έγγραφο ταυτοποίησης (ΥΔ ή Εξουσιοδότηση)');
        setIsSubmitting(false);
        return;
      }
      
      if (!formData.requestType) {
        toast.error('Παρακαλώ επιλέξτε είδος αιτήματος');
        setIsSubmitting(false);
        return;
      }

      if (!formData.email) {
        toast.error('Παρακαλώ συμπληρώστε το email επικοινωνίας');
        setIsSubmitting(false);
        return;
      }

      // Debug logging
      console.log('📤 Submitting request with data:', {
        requester_type: formData.requesterType,
        request_type: formData.requestType,
        identity_method: formData.identityMethod,
        email: formData.email,
        attachments: attachmentTypes.length
      });

      // Use relative path (Apache proxy handles routing)
      // In development, use webpack proxy or modify for local testing
      const apiUrl = '/api/requests';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formDataToSend
      });

      const result = await response.json();

      if (result.success) {
        // Save protocol number if available
        if (result.protocolNumber) {
          setProtocolNumber(result.protocolNumber);
        }
        setCurrentStep(6); // Success step (now step 6 with 5 form steps)
        toast.success('Το αίτημα καταχωρήθηκε επιτυχώς!');
      } else {
        throw new Error(result.error || 'Σφάλμα στην υποβολή');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.message || 'Σφάλμα στην υποβολή του αιτήματος');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    if (currentStep === 6) {
      return <SuccessStep resetForm={resetForm} protocolNumber={protocolNumber} />;
    }

    const StepComponent = steps.find(step => step.id === currentStep)?.component;
    if (!StepComponent) return null;

    return (
      <StepComponent
        formData={formData}
        updateFormData={updateFormData}
        nextStep={nextStep}
        prevStep={prevStep}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
      />
    );
  };

  const isStepCompleted = (stepId) => {
    // Ένα step είναι completed μόνο αν έχουμε περάσει από αυτό
    return currentStep > stepId;
  };

  const canProceed = () => {
    // Step 1: Στοιχεία Αιτούντος
    if (currentStep === 1) {
      return formData.requesterType;
    }
    
    // Step 2: Δικαιολογητικά
    if (currentStep === 2) {
      if (!formData.identityMethod) return false;
      // Χρειάζεται αρχείο επισύναψης (ΥΔ ή Εξουσιοδότηση)
      return formData.identityDocument !== null;
    }
    
    // Step 3: Είδος Αιτήματος
    if (currentStep === 3) {
      return formData.requestType && formData.requestType.length > 0;
    }
    
    // Step 4: Στοιχεία Αιτήματος (conditional validation based on request type)
    if (currentStep === 4) {
      // Για DL13 (σχόλια) και DL14 (άλλο) δεν χρειάζεται ΑΜΚΑ
      if (['DL13', 'DL14'].includes(formData.requestType)) {
        return formData.email && formData.phone;
      }
      
      // Για DL2 (θανόντος) χρειάζονται επιπλέον έγγραφα
      if (formData.requestType === 'DL2') {
        return formData.patientAmka && formData.email && formData.phone && 
               formData.familyCertificate && formData.willCertificate;
      }
      
      // Για όλα τα άλλα χρειάζεται τουλάχιστον ΑΜΚΑ και email
      return formData.patientAmka && formData.email && formData.phone;
    }
    
    // Step 5: Επιβεβαίωση
    if (currentStep === 5) {
      return true; // Μπορεί πάντα να προχωρήσει στην υποβολή
    }
    
    return false;
  };

  return (
    <div className="form-wizard">
      <div className="wizard-header">
        <p>Συμπληρώστε τα παρακάτω βήματα για την υποβολή του αιτήματός σας</p>
      </div>

      <div className="wizard-steps">
        {steps.map((step) => (
          <div
            key={step.id}
            className={`step ${
              currentStep === step.id ? 'active' : ''
            } ${
              isStepCompleted(step.id) ? 'completed' : ''
            }`}
          >
            <div className="step-number">
              {step.id}
            </div>
            <div className="step-label">{step.title}</div>
          </div>
        ))}
      </div>

      <div className="step-content">
        {renderStep()}
      </div>

      {currentStep <= 5 && (
        <div className="step-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={prevStep}
            disabled={currentStep === 1}
          >
            Προηγούμενο
          </button>

          {currentStep < 5 ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={nextStep}
              disabled={!canProceed()}
            >
              Επόμενο
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-success"
              onClick={handleSubmit}
              disabled={!canProceed() || isSubmitting}
            >
              {isSubmitting ? 'Υποβολή...' : 'Υποβολή Αιτήματος'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FormWizard;
